import { Link } from 'react-router-dom';
import { Brain, CheckCircle, AlertCircle, XCircle, CreditCard, Mail, ArrowRight } from 'lucide-react';

export default function TesterGuide() {
  const works = [
    'Browse homepage, programs, marketplace, pricing — anyone, no signup',
    'Sign up via Google OAuth (one click)',
    'Complete profile (DOB + 3 consents) — adults activate instantly',
    'Play any of 5 free programs end-to-end (~1 hour each)',
    'See your BrainMap Score (0-900) generated after sessions',
    'View Cognitive Profile — radar chart, weak areas, AI feedback',
    'Activate Parent Dashboard, link a child account by email',
    'Settings → Export your data (JSON) or delete account',
    'Privacy Policy & Terms of Service'
  ];

  const partial = [
    {
      title: 'Razorpay Pro Subscription',
      hint: 'Test mode is live. Use test card 4111 1111 1111 1111, any future expiry, any 3-digit CVV. No real charge.',
      icon: CreditCard
    },
    {
      title: 'Parent Consent Email (under-18 signup)',
      hint: 'Email won\'t arrive (Resend sandbox). The verification URL is shown in-app — copy and share via WhatsApp/SMS.',
      icon: Mail
    }
  ];

  const broken = [
    'Real card payments (test mode only — by design)',
    'Welcome / weekly digest emails to non-verified addresses (DNS verification pending)'
  ];

  return (
    <div className="min-h-screen" data-testid="tester-guide-page">
      <section className="border-b border-[#09090B] bg-[#09090B] text-white py-16">
        <div className="container-swiss max-w-3xl">
          <p className="mono-data text-zinc-400 mb-2">For Friends & Beta Testers</p>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Try FOKYES — what works, what to expect
          </h1>
          <p className="text-zinc-400">
            Thanks for testing! Here's exactly what's ready for end-to-end testing and where to use workarounds.
          </p>
        </div>
      </section>

      <div className="container-swiss py-12 max-w-3xl space-y-10">
        {/* Works */}
        <section data-testid="works-section">
          <div className="flex items-center gap-3 mb-5">
            <CheckCircle className="w-6 h-6 text-[#09090B]" />
            <h2 className="text-2xl font-black tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>Works end-to-end</h2>
          </div>
          <ul className="space-y-2">
            {works.map((w, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-zinc-700">
                <span className="text-[#09090B] mt-0.5">✓</span>
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Partial */}
        <section data-testid="partial-section">
          <div className="flex items-center gap-3 mb-5">
            <AlertCircle className="w-6 h-6 text-zinc-700" />
            <h2 className="text-2xl font-black tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>Works with workarounds</h2>
          </div>
          <div className="space-y-4">
            {partial.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="border-l-4 border-zinc-400 bg-zinc-100 px-4 py-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-4 h-4 text-zinc-700" />
                    <h4 className="font-bold text-sm">{p.title}</h4>
                  </div>
                  <p className="text-xs text-zinc-700 leading-relaxed">{p.hint}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Broken */}
        <section data-testid="broken-section">
          <div className="flex items-center gap-3 mb-5">
            <XCircle className="w-6 h-6 text-[#09090B]" />
            <h2 className="text-2xl font-black tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>Not active yet</h2>
          </div>
          <ul className="space-y-2">
            {broken.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-zinc-600">
                <span className="text-[#09090B] mt-0.5">×</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Recommended journey */}
        <section className="border border-[#09090B] p-6" data-testid="journey-section">
          <h3 className="mono-data text-sm text-[#09090B] mb-4">Recommended 5-minute test journey</h3>
          <ol className="space-y-3 text-sm text-zinc-700 list-decimal pl-5">
            <li>Open the homepage → click <strong>Sign In</strong> → continue with Google</li>
            <li>Complete profile (use any DOB making you 18+ for fastest path)</li>
            <li>Go to <strong>Programs</strong> → pick <em>"Memory Architecture"</em> → start Session 1</li>
            <li>Complete a few blocks, submit attempt</li>
            <li>Visit <strong>BrainMap</strong> in the navbar → see your score generated</li>
            <li>Visit <strong>Pricing</strong> → click <em>Upgrade to Pro</em>, OR go to any premium program → <em>Buy Program</em></li>
            <li>
              In the Razorpay modal, use <strong>UPI</strong> with VPA <code className="bg-zinc-100 px-1.5 py-0.5 mono-data text-xs">success@razorpay</code> — it succeeds instantly with no OTP.
              <span className="block mt-1 text-xs text-zinc-500">
                Or pay by card: <code className="bg-zinc-100 px-1.5 py-0.5 mono-data text-xs">4111 1111 1111 1155</code> for one-time program purchase, or <code className="bg-zinc-100 px-1.5 py-0.5 mono-data text-xs">4386 2894 0766 0153</code> for Pro subscription (any future expiry, any CVV) → click <strong>Success</strong> on the 3D Secure simulator.
                <span className="block mt-0.5">Avoid <code className="bg-zinc-100 px-1 mono-data text-xs">4111…1111</code> (4 ones at the end) — Razorpay rejects it.</span>
              </span>
            </li>
          </ol>
          <Link to="/" className="inline-flex items-center gap-2 mt-6 text-sm font-bold text-[#09090B] hover:underline">
            Start now <ArrowRight className="w-4 h-4" />
          </Link>
        </section>

        {/* Feedback */}
        <section className="text-center py-8 border-t border-zinc-200">
          <Brain className="w-8 h-8 text-[#09090B] mx-auto mb-3" />
          <p className="text-sm text-zinc-600 mb-2">Found a bug? Have feedback?</p>
          <p className="text-xs text-zinc-400">Reply to whoever invited you to test — they'll forward it to the FOKYES team.</p>
        </section>
      </div>
    </div>
  );
}
